import { forwardRef } from 'react'
import { Box } from '@chakra-ui/react'
import { useFormColors } from '../../forms/FormThemeContext'
import { withFormLabel } from '../../hocs/withFormLabel'
import { withFormValidation } from '../../hocs/withFormValidation'
import { useResolveText } from '../../i18n'

interface SelectOption {
  value: string | number
  label?: string
  labelTid?: string
}

interface BaseSelectProps {
  value?: string | number
  onChange?: (value: string | number) => void
  onBlur?: () => void
  options: SelectOption[]
  placeholder?: string
  placeholderTid?: string
  disabled?: boolean
  name?: string
}

const BaseSelect = forwardRef<HTMLSelectElement, BaseSelectProps>(({ value, onChange, onBlur, options, placeholder, placeholderTid, disabled, ...rest }, ref) => {
  const c = useFormColors()
  const resolve = useResolveText()
  const resolvedPlaceholder = resolve(placeholderTid, placeholder)

  const selectStyles: React.CSSProperties = {
    width: '100%',
    padding: '0.75rem 2.5rem 0.75rem 1rem',
    borderRadius: '0.25rem',
    border: 'none',
    fontSize: '0.875rem',
    background: c.bgInput,
    color: c.text,
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.7 : 1,
  }

  return (
    <Box position="relative">
      <select
        ref={ref}
        value={value ?? ''}
        onChange={(e) => {
          const val = e.target.value
          const numVal = Number(val)
          onChange?.(isNaN(numVal) ? val : numVal)
        }}
        onBlur={onBlur}
        style={selectStyles}
        disabled={disabled}
        {...rest}
      >
        {resolvedPlaceholder && <option value="">{resolvedPlaceholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{resolve(opt.labelTid, opt.label)}</option>
        ))}
      </select>
      <Box
        position="absolute" right="0.75rem" top="50%" transform="translateY(-50%)"
        pointerEvents="none" color={c.textSecondary}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </Box>
    </Box>
  )
})
BaseSelect.displayName = 'BaseSelect'

export const LabeledSelect = withFormLabel(BaseSelect)
export const TrackedSelect = withFormValidation(LabeledSelect)
